import { useState } from 'react';
import { toast } from 'sonner';
import { useLoaderData } from 'react-router-dom';

import { SettingsSubPageLayout } from '../../components/settings/SettingsSubPageLayout';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { accountAPI } from '../../data/account';
import { settingsHubLoader } from '../../data/settings';
import pt from '../../locales/pt';

export function AccountAccess() {
  const { user } = useLoaderData();
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(null);

  const onRequestEmailChange = async () => {
    setEmailLoading(true);
    const response = await accountAPI.requestEmailChange({
      new_email: newEmail.trim(),
      current_password: emailPassword,
    });
    setEmailLoading(false);
    if (!response.success) {
      toast.error(response.message);
      return;
    }
    setEmailSent(true);
    toast.success(pt.settings.account.emailChangeSent);
  };

  const onChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setPasswordError(pt.settings.account.passwordMismatch);
      return;
    }
    setPasswordError(null);
    setPasswordLoading(true);
    const response = await accountAPI.changePassword({
      old_password: oldPassword,
      new_password1: newPassword,
      new_password2: confirmPassword,
    });
    setPasswordLoading(false);
    if (!response.success) {
      toast.error(response.message);
      return;
    }
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    toast.success(pt.settings.account.passwordChanged);
  };

  return (
    <SettingsSubPageLayout
      section={pt.settings.account.section}
      title={pt.settings.account.title}
      subtitle={pt.settings.account.subtitle}
    >
      <div className="rounded-2xl border border-border bg-card p-4">
        <Label htmlFor="current-email">{pt.settings.account.emailLabel}</Label>
        <Input id="current-email" value={user?.email || ''} readOnly className="mt-2 bg-muted/40" />
        <p className="mt-2 text-xs text-muted-foreground">{pt.settings.account.emailHint}</p>

        {emailSent ? (
          <p className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100">
            {pt.settings.account.emailChangeSent}
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            <div>
              <Label htmlFor="new-email">{pt.settings.account.newEmailLabel}</Label>
              <Input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                autoComplete="email"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="email-password">{pt.settings.account.currentPasswordLabel}</Label>
              <PasswordInput
                id="email-password"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                autoComplete="current-password"
                className="mt-2"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={emailLoading || !newEmail.trim() || !emailPassword}
              onClick={onRequestEmailChange}
            >
              {pt.settings.account.requestEmailChange}
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-4">
        <h2 className="text-sm font-semibold text-foreground">{pt.settings.account.passwordSection}</h2>
        <div className="mt-4 space-y-3">
          <div>
            <Label htmlFor="old-password">{pt.settings.account.oldPasswordLabel}</Label>
            <PasswordInput
              id="old-password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="new-password">{pt.settings.account.newPasswordLabel}</Label>
            <PasswordInput
              id="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="confirm-password">{pt.settings.account.confirmPasswordLabel}</Label>
            <PasswordInput
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              className="mt-2"
            />
          </div>
          {passwordError ? <p className="text-sm text-error">{passwordError}</p> : null}
          <Button
            type="button"
            disabled={passwordLoading || !oldPassword || !newPassword || !confirmPassword}
            onClick={onChangePassword}
          >
            {pt.settings.account.submitPassword}
          </Button>
        </div>
      </div>
    </SettingsSubPageLayout>
  );
}

AccountAccess.loader = settingsHubLoader;
